<template>
    <div>
        <v-autocomplete
            v-model="localValue"
            :search-input="searchText"
            @update:search-input="searchText = $event"
            flat solo dense
            :placeholder="`Search for students (${MIN_SEARCH_LENGTH} character minimum)`"
            :items="filteredItems"
            itemText="student_full_name"
            itemValue="student_id"
            :loading="showLoadingIndicator"
            :hide-no-data="hideNoData"
            return-object
        >
            <template v-slot:item="data">
                <v-list-item-avatar v-if="data.item.student_id">
                    <v-img :src="getStudentImg(data.item.student_id)"/>
                </v-list-item-avatar>

                <v-list-item-content>

                    <v-list-item-title>{{data.item.student_full_name}}</v-list-item-title>
                    <v-list-item-subtitle>{{data.item.school_name}} {{data.item.grade_desc}}</v-list-item-subtitle>
                </v-list-item-content>
            </template>
            <template #append v-if="includeAdvanced">
                <span class="student-selector" @click="showingAdvancedSearch=true">Advanced</span>
            </template>
        </v-autocomplete>
        <fe-dialog v-if="includeAdvanced && showingAdvancedSearch"
            width="800px"
            persistent
            title="Advanced Search"
            dismissButtonText="Cancel"
            acceptButtonText="Add Students"
            @close="showingAdvancedSearch=false"
            @accept="addAdvancedStudentSelection"
            class="heresaclass"
            contentClass="heresanotherclass"
            :menuProps="{'contentClass': 'menupropsclass'}"
        >
            <student-search v-model="advancedSelection" :searchYear="schoolYearId ? {id: schoolYearId} : null" :maximumStudentsAllowed="maximumStudentsAllowed" :currentCount="currentCount" />
        </fe-dialog>
    </div>
</template>

<script>
    import lodash from 'lodash'
    import { mapState } from 'vuex'
    import StudentSearch from '@/components/common/StudentSearch'

    export default {
        name: 'StudentNameSearch',
        components: {StudentSearch},
        props: {
            value: {

            },
            /** a list of students to ignore when showing the dropdown or when selected again in the advanced search */
            ignoredStudents: {
                default: [],
            },
            /** maximum number of students allowed */
            maximumStudentsAllowed: {
                type: Number,
                default: null,
            },
            /** current number of students selected */
            currentCount: {
                type: Number,
                default: null,
            },
            /** school year to filter on when typing in search box */
            schoolYearId: {

            },
            /** school ID to filter on when typing in search box */
            schoolId: {
                default: null,
            },
            /** if we should include the advanced search option - caller must support multi-student selection */
            includeAdvanced: {
                type: Boolean,
                default: false,
            },
            /** if the input should be cleared after emitting the selection */
            clearOnSelect: {
                type: Boolean,
                default: false,
            },
            /** if we should return the ID(s) of the selected students or the entire student records */
            byId: {
                type: Boolean,
                default: false
            },
        },
        computed: {
            ...mapState('global', ['currentYear']),
            /**
             * filteredItems() returns the students loaded minus any we already selected
             */
            filteredItems() {
                return this.items.filter(item => !this.ignoredStudentMap.has(item.student_id))
            },
            ignoredStudentMap() {
                return new Map(this.ignoredStudents.map(v => v.hasOwnProperty('student_id') ? [v.student_id, true] : [v, true]))
            },
            hideNoData() {
                return !this.searchText || this.searchText.length < 3 || this.showLoadingIndicator || this.loadingDebounceCounter > 0
            },
            MIN_SEARCH_LENGTH: () => 3,
        },
        watch: {
            searchText(v) {
                if (this.schoolId) {
                    return; // schoolId will have loaded all the students
                }
                if (v && v.length >= this.MIN_SEARCH_LENGTH) {
                    this.loadingDebounceCounter++
                } else {
                    this.items = []
                }
            },
            loadingDebounceCounter: lodash.debounce(function (counter) {
                if (counter > 0) {
                    this.doSearch()
                }
            }, 500),
            localValue(v) {
                if (v && !this.ignoredStudentMap.has(v.student_id)) {
                    let selectedStudent = this.byId ? v.student_id : v
                    this.$emit('input', selectedStudent)
                    this.$emit('change', selectedStudent)
                }
                if (this.clearOnSelect) {
                    this.$nextTick(() => {
                        this.localValue = null
                    })
                }
            },
            schoolId(newVal, oldVal) {
                if (newVal != oldVal) {
                    this.items = [] // clear search results so we're not showing students from the old school
                    if (newVal) {
                        this.doSearch()
                    }
                }
            },
            schoolYearId(v) {
                this.searchYearId = this.schoolYearId || this.currentYear.id
            },
        },
        created() {
            this.localValue = this.value
            this.searchYearId = this.schoolYearId || this.currentYear.id
            if (this.value) {
                this.items.push(this.value)
                this.$nextTick(() => { this.initialized = true })
            }
            if (this.schoolId) {
                this.doSearch()
            }
        },
        data() {
            return {
                searchText: '',
                searchYearId: '',
                localValue: null,
                items: [],
                showLoadingIndicator: false,
                loadingDebounceCounter: 0,
                advancedSelection: [],
                initialized: false,
                showingAdvancedSearch: false,
            }
        },
        methods: {
            doSearch() {
                // if (!this.initialized) return
                this.showLoadingIndicator = true
                const params = {
                    name: this.searchText || '',
                    school_year_id: this.searchYearId,
                    school_id: this.schoolId || '',
                }
                this.$modelGet('studentGradeHistory', params).then(response => {
                    if ((this.schoolId || '') == params.school_id
                        && this.searchYearId == params.school_year_id
                        && this.schoolId || (
                            (this.searchText || '').length >= this.MIN_SEARCH_LENGTH
                            && (this.searchText || '').toLowerCase().includes(params.name.toLowerCase())
                        )
                    ) {
                        // user didn't change the search criteria while the search was in progress - update the items
                        // this prevents stale data from being shown
                        this.items = response
                    }
                }).finally(() => {
                    this.showLoadingIndicator = false
                    this.loadingDebounceCounter = 0
                })
            },
            getStudentImg(student_id) {
                return this.$axios.defaults.baseURL + this.$models.getUrl('studentImage', 'read') + '&id=' + student_id
            },
            addAdvancedStudentSelection() {
                if (!this.advancedSelection.length) return
                const selectedStudents = this.advancedSelection.filter(selectedStudent => !this.ignoredStudentMap.has(selectedStudent.student_id)).map(x => this.byId ? x.student_id : x)
                this.$emit('input', selectedStudents)
                this.$emit('change', selectedStudents)
               if (this.clearOnSelect) {
                    this.$nextTick(() => {
                        this.advancedSelection = []
                    })
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
.student-selector {
    font-weight: 500;
    cursor: pointer;
    text-decoration: underline;
    color: rgb(0, 108, 144);
}
</style>
